import React, { useState, useEffect } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { CalendarOutlined, CloseOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';
import moment from 'moment';
import { useNavigate, useParams } from 'react-router-dom';

import { EPButton } from '@/components';
import { useMeasurement } from '@/hooks/useMeasurement';
import { formatThousands } from '@/utils/util-functions';

import {
  ModalContainer,
  ModalHeader,
  ModalHeaderTitle,
  ModalHeaderActions,
  ModalContent,
  ModalBody,
  ModalField,
  ModalFieldLabel,
  ModalFieldValue,
  ModalActions,
  MinimizedContainer,
} from './styled';
import MinimizeIcon from '@/comp/common/icons/MinimizeIcon';
import MaximizeIcon from '@/comp/common/icons/MaximizeIcon';
import EPIconButton from '@/components/EPButton/EPIconButton';
import { LeakDetectionPeriodStatus } from '@/store/LeakageStore';
import { useStores } from '@/hooks/use-stores';
import { permissions } from '@/constants';

interface ReviewLeakAuditModalProps {
  open: boolean;
  onClose: () => void;
  startDate?: string;
  endDate?: string;
  leakageId?: string;
  onUpdateAndPublish?: () => Promise<void>;
  onRejectAudit?: () => Promise<void>;
}

interface LeakAuditData {
  measuredPeriodStart?: string;
  measuredPeriodEnd?: string;
  preceding3MonthPower?: number;
  preceding3MonthFlow?: number;
  actualLeakRate?: number;
  averageFlowDuringLeak?: number;
  averagePressureDuringLeak?: number;
}

const ReviewLeakAuditModal = observer(
  ({ open, onClose, startDate, endDate, leakageId, onUpdateAndPublish, onRejectAudit }: ReviewLeakAuditModalProps) => {
    const { t } = useTranslation();
    const { tvalue, tunit, measurementSystem } = useMeasurement();
    const navigate = useNavigate();
    const { stationId } = useParams();
    const [isMinimized, setIsMinimized] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isRejecting, setIsRejecting] = useState(false);
    const [leakAuditData, setLeakAuditData] = useState<LeakAuditData>({});
    // Store actualLeakRate in user's unit for input field (no conversion during typing)
    const [actualLeakRateUserUnit, setActualLeakRateUserUnit] = useState<number | undefined>(undefined);
    const [canDisplayPublishAndRejectButtons, setCanDisplayPublishAndRejectButtons] = useState(false);
    const [canDisplayCloseButton, setCanDisplayCloseButton] = useState(true);
    const { leakageStore, permissionStore } = useStores();

    // Initialize dates from props or use current dates
    const [dateRange, setDateRange] = useState<[moment.Moment | null, moment.Moment | null]>(() => {
      if (startDate && endDate) {
        return [moment(startDate), moment(endDate)];
      }
      return [null, null];
    });

    useEffect(() => {
      if (startDate && endDate) {
        setDateRange([moment(startDate), moment(endDate)]);
      }
    }, [startDate, endDate]);

    // Reset minimized state when modal opens
    useEffect(() => {
      if (open) {
        setIsMinimized(false);
      }
    }, [open]);

    // Fetch leak audit data when modal opens
    useEffect(() => {
      if (open && dateRange[0] && dateRange[1]) {
        const fetchLeakAuditData = async () => {
          try {
            await leakageStore.getLeakDetectionPeriod(leakageId);
            const leakDetectionPeriod = leakageStore.leakDetectionPeriod;
            const metricLeakRate = leakDetectionPeriod.leakRate ?? 0;
            // Store data in METRIC (for saving)
            setLeakAuditData({
              preceding3MonthPower: leakDetectionPeriod.totalPowerKw ?? 0,
              preceding3MonthFlow: leakDetectionPeriod.totalFlowM3Min ?? 0,
              actualLeakRate: metricLeakRate,
              averageFlowDuringLeak: leakDetectionPeriod.averageFlowM3Min ?? 0,
              averagePressureDuringLeak: leakDetectionPeriod.averagePressureBar ?? 0,
            });
            // Convert to user's unit for input field display
            const userUnitValue = tvalue(metricLeakRate, 'm^3/min', measurementSystem, 'METRIC');
            setActualLeakRateUserUnit(userUnitValue);

            // Update button visibility after data is fetched
            const canDisplay =
              leakDetectionPeriod?.status === LeakDetectionPeriodStatus.Draft &&
              permissionStore.isAuth(permissions.CREATE_STATION_VALUE_DASHBOARD_LEAKAGE);
            setCanDisplayPublishAndRejectButtons(canDisplay);
            setCanDisplayCloseButton(!canDisplay);
          } catch (error) {
            console.error('Error fetching leak audit data:', error);
          }
        };
        fetchLeakAuditData();
      }
    }, [open, dateRange, leakageId]);

    const handleMinimize = () => {
      setIsMinimized(true);
    };

    const handleMaximize = () => {
      setIsMinimized(false);
    };

    const toStoredMetricLeakRate = (value: number | undefined): number => {
      if (value === undefined || value === null || isNaN(value)) return 0;
      if (measurementSystem === 'IMPERIAL') {
        return convertToMetric(value, 'cfm') ?? 0;
      }
      return value;
    };

    const convertToMetric = (value: number, unit: string) => {
      if (value === undefined || value === null || isNaN(value)) return undefined;
      return tvalue(value, unit, 'METRIC', measurementSystem);
    };

    const convertFromMetric = (value: number, unit: string) => {
      if (value === undefined || value === null) return undefined;
      return tvalue(value, unit, measurementSystem, 'METRIC');
    };

    const formatDatesForPayload = (): { startDate: string; endDate: string } | null => {
      if (!dateRange[0] || !dateRange[1]) {
        return null;
      }
      const start = dateRange[0];
      const end = dateRange[1];
      return {
        startDate: moment.utc({ year: start.year(), month: start.month(), date: start.date() }).toISOString(),
        endDate: moment.utc({ year: end.year(), month: end.month(), date: end.date() }).endOf('day').toISOString(),
      };
    };

    const handleUpdateAndPublish = async () => {
      const formattedDates = formatDatesForPayload();
      if (!formattedDates) {
        return;
      }

      setIsUpdating(true);
      try {
        if (onUpdateAndPublish) {
          await onUpdateAndPublish();
        } else {
          const metricValue = toStoredMetricLeakRate(actualLeakRateUserUnit);

          const payload = {
            id: leakageId,
            actualLeakRateFlowM3Min: metricValue,
            ...formattedDates,
            status: LeakDetectionPeriodStatus.Published,
          };

          await leakageStore.updateAndPublishLeakDetectionPeriod(payload);
        }

        message.success(
          <div>
            <b>
              {t('leakAuditUpdatedAndPublishedSuccessfully', {
                defaultValue: 'Leak draft has been published to the customer.',
              })}
            </b>{' '}
            <a
              onClick={() => {
                navigate(`/home/station/${stationId}/value/power#leakage-timeline`);
              }}
            >
              {t('viewLeak', { defaultValue: 'View Leak' })}
            </a>
          </div>,
          5,
        );

        setTimeout(() => {
          onClose();
        }, 100);
      } catch (error) {
        message.error(t('errorUpdatingLeakAudit', { defaultValue: 'Error updating leak audit' }));
      } finally {
        setIsUpdating(false);
      }
    };

    const handleRejectAudit = async () => {
      const formattedDates = formatDatesForPayload();
      if (!formattedDates) {
        return;
      }

      setIsRejecting(true);
      try {
        if (onRejectAudit) {
          await onRejectAudit();
        } else {
          const metricValue = toStoredMetricLeakRate(actualLeakRateUserUnit);

          const payload = {
            id: leakageId,
            actualLeakRateFlowM3Min: metricValue,
            ...formattedDates,
            rejectReason: 'Rejected by user',
            status: LeakDetectionPeriodStatus.Rejected,
          };
          await leakageStore.updateAndPublishLeakDetectionPeriod(payload);
        }

        message.success(
          <div>
            <b>{t('leakAuditRejectedSuccessfully', { defaultValue: 'Leak audit rejected successfully' })}</b>{' '}
            <a
              onClick={() => {
                navigate(`/home/station/${stationId}/value/power#leakage-timeline`);
              }}
            >
              {t('viewLeak', { defaultValue: 'View Leak' })}
            </a>
          </div>,
          5,
        );
      } catch (error) {
        message.error(t('errorRejectingLeakAudit', { defaultValue: 'Error rejecting leak audit' }));
      } finally {
        setIsRejecting(false);
      }

      onClose();
    };

    const formatDateRange = () => {
      if (!dateRange[0] || !dateRange[1]) {
        return '';
      }
      return `${dateRange[0].format('MMM D, YYYY')} - ${dateRange[1].format('MMM D, YYYY')}`;
    };

    if (!open) {
      return null;
    }

    if (isMinimized) {
      return (
        <MinimizedContainer onClick={handleMaximize}>
          <ModalHeaderTitle>{t('reviewLeakAudit', { defaultValue: 'Review Leak Audit' })}</ModalHeaderTitle>
          <ModalHeaderActions>
            <EPIconButton
              icon={<MaximizeIcon />}
              onClick={e => {
                e.stopPropagation();
                handleMaximize();
              }}
            />
            <EPIconButton
              icon={<CloseOutlined />}
              onClick={e => {
                e.stopPropagation();
                onClose();
              }}
            />
          </ModalHeaderActions>
        </MinimizedContainer>
      );
    }

    return (
      <ModalContainer
        open={open}
        onCancel={onClose}
        mask={false}
        maskClosable={false}
        closable={false}
        footer={null}
        width={420}
        style={{ top: 20, right: 20 }}
        styles={{
          body: { padding: 0 },
        }}
      >
        <ModalHeader>
          <ModalHeaderTitle>{t('reviewLeakAudit', { defaultValue: 'Review Leak Audit' })}</ModalHeaderTitle>
          <ModalHeaderActions>
            <EPIconButton icon={<MinimizeIcon />} onClick={handleMinimize} />
            <CloseOutlined onClick={onClose} />
          </ModalHeaderActions>
        </ModalHeader>
        <ModalContent>
          <ModalBody>
            <ModalField>
              <ModalFieldLabel>
                <CalendarOutlined style={{ marginRight: 8 }} />
                {t('Measured Period', { defaultValue: 'Measured Period' })}:
              </ModalFieldLabel>
              <ModalFieldValue>{formatDateRange()}</ModalFieldValue>
            </ModalField>

            <ModalField>
              <ModalFieldLabel>
                {t('Preceding 3 Month Power', { defaultValue: 'Preceding 3 Month Power' })}:
              </ModalFieldLabel>
              <ModalFieldValue>
                {leakAuditData.preceding3MonthPower !== undefined
                  ? `${formatThousands(tvalue(leakAuditData.preceding3MonthPower, 'kw'))} ${tunit('kW')}`
                  : 'N/A'}
              </ModalFieldValue>
            </ModalField>

            <ModalField>
              <ModalFieldLabel>
                {t('Preceding 3 Month Flow', { defaultValue: 'Preceding 3 Month Flow' })}:
              </ModalFieldLabel>
              <ModalFieldValue>
                {leakAuditData.preceding3MonthFlow !== undefined
                  ? `${formatThousands(convertFromMetric(leakAuditData.preceding3MonthFlow, 'm^3/min'))} ${tunit(
                      'm^3/min',
                    )}`
                  : 'N/A'}
              </ModalFieldValue>
            </ModalField>

            <ModalField>
              <ModalFieldLabel>{t('Actual Leak Rate', { defaultValue: 'Actual Leak Rate' })}:</ModalFieldLabel>
              {canDisplayPublishAndRejectButtons && (
                <ModalFieldValue>
                  <input
                    className="input-field"
                    type="number"
                    value={actualLeakRateUserUnit !== undefined ? actualLeakRateUserUnit : ''}
                    inputMode="decimal"
                    onChange={e => {
                      const userValue = e.target.value === '' ? undefined : Number(e.target.value);
                      setActualLeakRateUserUnit(userValue);
                    }}
                    style={{ textAlign: 'right' }}
                  />
                  <span style={{ marginLeft: 8 }}>{tunit('m^3/min')}</span>
                </ModalFieldValue>
              )}
              {!canDisplayPublishAndRejectButtons && (
                <ModalFieldValue>
                  {leakAuditData.actualLeakRate !== undefined
                    ? `${formatThousands(convertFromMetric(leakAuditData.actualLeakRate, 'm^3/min'))} ${tunit(
                        'm^3/min',
                      )}`
                    : 'N/A'}
                </ModalFieldValue>
              )}
            </ModalField>
            <ModalField>
              <ModalFieldLabel>
                {t('Average Flow During Leak', { defaultValue: 'Average Flow During Leak' })}:
              </ModalFieldLabel>
              <ModalFieldValue>
                {leakAuditData.averageFlowDuringLeak !== undefined
                  ? `${formatThousands(convertFromMetric(leakAuditData.averageFlowDuringLeak, 'm^3/min'))} ${tunit(
                      'm^3/min',
                    )}`
                  : 'N/A'}
              </ModalFieldValue>
            </ModalField>

            <ModalField>
              <ModalFieldLabel>
                {t('Average Pressure During Leak', { defaultValue: 'Average Pressure During Leak' })}:
              </ModalFieldLabel>
              <ModalFieldValue>
                {leakAuditData.averagePressureDuringLeak !== undefined
                  ? `${formatThousands(convertFromMetric(leakAuditData.averagePressureDuringLeak, 'bar'))} ${tunit(
                      'bar',
                    )}`
                  : 'N/A'}
              </ModalFieldValue>
            </ModalField>
          </ModalBody>

          <ModalActions>
            {canDisplayPublishAndRejectButtons && (
              <>
                <EPButton onClick={handleRejectAudit} type="text" loading={isRejecting} size="medium">
                  {t('rejectAudit', { defaultValue: 'Reject Audit' })}
                </EPButton>
                <EPButton onClick={handleUpdateAndPublish} type="primary" loading={isUpdating} size="medium">
                  {t('updateAndPublish', { defaultValue: 'Update & Publish' })}
                </EPButton>
              </>
            )}
            {canDisplayCloseButton && (
              <EPButton onClick={onClose} type="primary" size="medium">
                {t('Close', { defaultValue: 'Close' })}
              </EPButton>
            )}
          </ModalActions>
        </ModalContent>
      </ModalContainer>
    );
  },
);

export default ReviewLeakAuditModal;
